<template>
    <div
        class="relative bg-white rounded-lg shadow-md p-4 flex flex-col sm:flex-row items-start sm:items-center sm:justify-between hover:shadow-lg transition gap-4">
        <!-- Badge: Positioned to Top-Right -->
        <div class="absolute top-2 right-2 z-10">
            <BaseBadge :status="dataBooking?.Status" />
        </div>

        <!-- Main Content -->
        <div class="flex flex-col sm:flex-row items-start sm:items-center w-full gap-4">
            <!-- Image -->
            <div class="w-full sm:w-32 h-32 flex-shrink-0">
                <LazyImage :src="dataBooking.BookDetails?.[0]?.Images?.[0]?.Filename"
                    :alt="dataBooking.BookDetails?.[0]?.Images?.[0]?.Filename || 'Room Image'"
                    class="w-full h-full object-cover rounded-lg" />
            </div>

            <!-- Booking Details -->
            <div class="flex-grow">
                <h3 class="text-xs md:text-sm text-gray-500 mb-1">
                    {{ $formatters.formatLongDate(dataBooking?.CreatedOn) }}
                </h3>
                <p class="text-xs font-semibold text-slate-600 mb-1">
                    Booking Code: {{ dataBooking?.Code || "-" }}
                </p>
                <h3 class="text-lg md:text-xl font-semibold text-gray-800 mb-1">
                    {{ dataBooking.BookDetails?.[0]?.Room?.Name || $t('bookingHistory.unknownRoomType') }}
                </h3>
                <p class="text-sm text-gray-500 mb-1">
                    {{ dataBooking.BookDetails?.[0]?.CityName || $t('bookingHistory.unknownCity') }} -
                    {{ dataBooking.BookDetails?.[0]?.PropertyName || $t('bookingHistory.unknownProperty') }}
                </p>
                <p class="text-sm text-blue-500 flex items-center mb-1">
                    <AppIcon icon="mdi:calendar" class="mr-2 w-4 h-4" />
                    {{ $formatters.formatDate(dataBooking.BookDetails?.[0]?.CheckIn) ||
                        $t('bookingHistory.unknownCheckIn')
                    }} -
                    {{ $formatters.formatDate(dataBooking.BookDetails?.[0]?.CheckOut) ||
                        $t('bookingHistory.unknownCheckOut') }}
                </p>
                <p class="text-sm text-gray-500 flex items-center">
                    <AppIcon icon="mdi:account" class="mr-2 w-4 h-4" />
                    {{ $t('bookingHistory.guests') }} {{ dataBooking.BookDetails?.[0]?.Pax || $t('notAvailable') }}
                </p>
                <div class="mt-2 flex flex-wrap items-center gap-2">
                    <div
                        v-if="showPhasePill && phaseLabel"
                        class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold"
                        :class="phasePillClass"
                    >
                        {{ phaseLabel }}
                    </div>
                    <div
                        class="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-semibold text-indigo-700"
                    >
                        {{ orderTypeLabel }}
                    </div>
                    <div
                        v-if="isExtension"
                        class="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-1 text-xs font-semibold text-purple-700"
                    >
                        Extend Booking
                    </div>
                    <div
                        v-if="hasPendingRescheduleUnpaid"
                        class="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800"
                    >
                        Reschedule Unpaid
                    </div>
                    <div
                        v-if="hasPendingChangeRoomUnpaid"
                        class="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800"
                    >
                        Change Room Unpaid
                    </div>
                    <div
                        v-if="hasPendingExtendUnpaid"
                        class="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800"
                    >
                        Extend Unpaid
                    </div>
                </div>
            </div>
        </div>

        <!-- Buttons -->
        <div class="flex flex-row flex-wrap justify-end gap-2 w-full sm:w-auto mt-6 sm:mt-4">
            <BaseButton v-if="canSendReview" type="primary" class="py-2 px-4 flex flex-row gap-2"
                @click.stop="openSendReviewModal(dataBooking.Code)"
                :disabled="isAllReviewed">
                <AppIcon icon="mdi:star" class="w-4 h-4" />
                <p>{{ $t('bookingHistory.sendReview') }}</p>
            </BaseButton>
            <BaseButton type="primary" class="py-2 px-4" @click.stop="handleDetailClick(dataBooking.Code)">
                {{ $t('bookingHistory.details') }}
            </BaseButton>
            <BaseButton v-if="allowDownloadReport" type="primary" class="py-2 px-4 flex items-center"
                @click.stop="handleDownloadReceipt(dataBooking.Code)">
                <AppIcon icon="mdi:download" class="mr-2" />
                {{ $t('bookingHistory.downloadReceipt') }}
            </BaseButton>
        </div>

        <!-- Modal -->
        <BaseModal v-if="openModalDetail" :title="$t('bookingHistory.paymentSummaryTitle')"
            @close="closePaymentSummary">
            <PaymentSummaryModal :bookingCode="selectedBookingCode" :isHistoryOrder="historyMode"
                @close="closePaymentSummary" />
        </BaseModal>

        <BaseModal v-if="isReviewModalOpen" :title="$t('bookingHistory.sendReview')" @close="closeSendReviewModal">
            <div>
                <!-- Pilihan BookDetails -->
                <div class="mb-4">
                    <label class="block mb-2 font-semibold">
                        {{ $t('propertyDetail.selectRoom') || 'Select Room' }}
                    </label>
                    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div v-for="detail in dataBooking.BookDetails" :key="detail.Id" :class="[
                            'border rounded-lg p-4 shadow-sm cursor-pointer hover:shadow-md transition',
                            selectedBookingDetailId === detail.Id ? 'border-blue-500 bg-blue-50' : 'border-gray-300',
                            selectedBookingDetailId && selectedBookingDetailId !== detail.Id ? 'opacity-50 cursor-not-allowed' : '',
                            detail.UserRating !== null ? 'opacity-50 cursor-not-allowed' : ''
                        ]" :disabled="selectedBookingDetailId && selectedBookingDetailId !== detail.Id || detail.UserRating !== null"
                            @click="toggleBookingDetailSelection(detail.Id)">
                            <!-- Image -->
                            <LazyImage :src="detail.Images?.[0]?.Filename"
                                :alt="detail.Images?.[0]?.Filename || 'Room Image'"
                                class="w-full h-24 object-cover rounded-md mb-2" />
                            <!-- Room Details -->
                            <h3 class="font-semibold text-gray-700 mb-1">
                                {{ detail.Room?.Name || $t('bookingHistory.unknownRoomType') }}
                            </h3>
                            <p class="text-sm text-gray-500">
                                {{ $formatters.formatDate(detail.CheckIn) || $t('bookingHistory.unknownCheckIn') }} -
                                {{ $formatters.formatDate(detail.CheckOut) || $t('bookingHistory.unknownCheckOut') }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- List Room Rating Types in Two Columns -->
                <div v-if="detail?.UserRating">
                    class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div v-for="type in selectedDetail.UserRating.UserRatingDetails" :key="type.Id" class="mb-4">
                        <!-- Name of Rating Type -->
                        <label class="block mb-2 font-semibold">
                            {{ type.Name }}
                        </label>
                        <!-- Stars for the Rating -->
                        <div class="flex gap-4 flex-row items-center justify-start">
                            <AppIcon v-for="starIndex in 5" :key="starIndex"
                                :icon="starIndex <= type.Rating ? 'mdi:star' : 'mdi:star-outline'"
                                class="w-8 h-8 text-yellow-500" />
                        </div>
                    </div>
                </div>

                <!-- Editable Mode -->
                <div v-if="selectedBookingDetailId && !selectedDetail?.UserRating"
                    class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div v-for="type in roomRatingTypes" :key="type.Id" class="mb-4">
                        <!-- Name of Rating Type -->
                        <label class="block mb-2 font-semibold">
                            {{ type.Name }}
                        </label>
                        <!-- Stars for the Rating -->
                        <div class="flex gap-4 flex-row items-center justify-start">
                            <span v-for="starIndex in 5" :key="starIndex" class="cursor-pointer">
                                <AppIcon icon="mdi:star" class="w-8 h-8" :class="{
                                    'text-yellow-500': (hoverRatings[type.Id] || selectedRatings[type.Id]) >= starIndex,
                                    'text-gray-300': !(hoverRatings[type.Id] || selectedRatings[type.Id]) || (hoverRatings[type.Id] || selectedRatings[type.Id]) < starIndex
                                }" @mouseover="hoverRatings[type.Id] = starIndex"
                                    @mouseleave="hoverRatings[type.Id] = 0"
                                    @click="selectedRatings[type.Id] = starIndex" />
                            </span>

                            <!-- Rating Description -->
                            <p class="text-sm mt-2 font-bold text-blue-500">
                                {{ ratingDescriptions[hoverRatings[type.Id] || selectedRatings[type.Id]] || '' }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Comment Input -->
                <div v-if="selectedBookingDetailId" class="mb-4">
                    <label for="comment" class="block mb-2 font-semibold">
                        {{ $t('propertyDetail.addComment') || 'Add Comment' }}
                    </label>
                    <textarea id="comment" v-model="reviewComment" :disabled="selectedDetail?.UserRating"
                        class="w-full border border-gray-300 rounded-lg p-2 resize-none" rows="4"
                        :placeholder="$t('propertyDetail.minLengthError') || 'Comment must be at least 50 characters.'"></textarea>
                    <p v-if="!isCommentValid && !selectedDetail?.UserRating" class="text-red-500 text-sm mt-1">
                        {{ commentError }}
                    </p>
                </div>

                <!-- Submit Button -->
                <div class="flex justify-end gap-2" v-if="!selectedDetail?.UserRating">
                    <BaseButton type="secondary" @click="closeSendReviewModal">
                        {{ $t('button.cancel') || 'Cancel' }}
                    </BaseButton>
                    <BaseButton type="primary" :disabled="!isFormValid" @click="submitReview">
                        {{ $t('bookingHistory.sendReview') || 'Send Review' }}
                    </BaseButton>
                </div>
            </div>
        </BaseModal>
    </div>
</template>

<script>
import { mapActions } from "vuex";

const CHANGE_ROOM_REQUEST_STORAGE_KEY = "ch_customer_change_room_requests";
const safeParseArray = (value) => {
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        return [];
    }
};

export default {
    props: {
        dataBooking: {
            type: Object,
            required: true,
        },
        historyMode: {
            type: Boolean,
            default: false,
        },
        allowDownloadReport: {
            type: Boolean,
            default: true,
        },
        openDetailInPage: {
            type: Boolean,
            default: false,
        },
        showPhasePill: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            isReviewModalOpen: false, // Kontrol modal
            selectedBookingDetailId: null, // Selected BookDetail ID
            roomRatingTypes: [], // List rating types
            reviewComment: "", // Komentar default
            selectedRatings: {}, // Penyimpanan nilai rating berdasarkan RoomRatingTypeId
            hoverRatings: {}, // Efek hover untuk nilai rating sementara
            ratingDescriptions: {
                1: this.$t('propertyDetail.veryPoor') || 'Sangat Buruk',
                2: this.$t('propertyDetail.poor') || 'Buruk',
                3: this.$t('propertyDetail.average') || 'Cukup',
                4: this.$t('propertyDetail.good') || 'Baik',
                5: this.$t('propertyDetail.excellent') || 'Sangat Baik',
            },

            openModalDetail: false, // Kontrol modal
            selectedBookingCode: null, // Kode booking yang dipilih
        };
    },
    computed: {
        isAllReviewed() {
            // Periksa apakah semua UserRating di BookDetails tidak null
            return this.dataBooking.BookDetails.every(detail => detail.UserRating !== null);
        },
        selectedDetail() {
            return this.dataBooking.BookDetails.find(detail => detail.Id === this.selectedBookingDetailId) || null;
        },
        canSendReview() {
            if (this.historyMode) {

                // check paid=4
                if (this.dataBooking.Status != 4) {
                    return false;
                }

                return true;
            }

            const checkOutDate = new Date(this.dataBooking.BookDetails?.[0]?.CheckOut);
            const today = new Date();

            // Periksa apakah CheckOut sudah lewat
            return today > checkOutDate;
        },
        isCommentValid() {
            return this.reviewComment.trim().length >= 50;
        },
        isFormValid() {
            return this.isCommentValid && Object.keys(this.selectedRatings).length === this.roomRatingTypes.length;
        },
        commentError() {
            return this.isCommentValid
                ? null
                : this.$t("propertyDetail.maxLengthError") || "Description must be at least 500 characters.";
        },
        bookingPhase() {
            const details = this.dataBooking?.BookDetails || [];
            const checkInTimes = details
                .map((detail) => new Date(detail.CheckIn).getTime())
                .filter((value) => !Number.isNaN(value));
            const checkOutTimes = details
                .map((detail) => new Date(detail.CheckOut).getTime())
                .filter((value) => !Number.isNaN(value));

            const start = checkInTimes.length ? Math.min(...checkInTimes) : null;
            const end = checkOutTimes.length ? Math.max(...checkOutTimes) : null;
            const now = Date.now();

            if (!start) return null;
            if (now < start) return "upcoming";
            if (end && now < end) return "in-stay";
            return null;
        },
        phaseLabel() {
            const tWithFallback = (key, fallback) => {
                const translated = this.$t(key);
                return translated && translated !== key ? translated : fallback;
            };
            if (this.bookingPhase === "in-stay") {
                return tWithFallback("ongoingOrder.phase.inStay", "In Stay");
            }
            if (this.bookingPhase === "upcoming") {
                return tWithFallback("ongoingOrder.phase.upcoming", "Upcoming");
            }
            return null;
        },
        phasePillClass() {
            if (this.bookingPhase === "in-stay") {
                return "bg-emerald-100 text-emerald-700";
            }
            if (this.bookingPhase === "upcoming") {
                return "bg-blue-100 text-blue-700";
            }
            return "bg-slate-100 text-slate-700";
        },
        orderTypeLabel() {
            const periodText = String(this.dataBooking?.PeriodText || this.dataBooking?.BookDetails?.[0]?.PeriodText || "").trim();
            if (periodText) {
                const lower = periodText.toLowerCase();
                if (lower.includes("hari") || lower.includes("daily")) return "Harian";
                if (lower.includes("bulan") || lower.includes("month")) return "Bulanan";
                if (lower.includes("tahun") || lower.includes("year") || lower.includes("annual")) return "Tahunan";
            }
            const period = Number(this.dataBooking?.Period ?? this.dataBooking?.BookDetails?.[0]?.Period ?? 1);
            if (period === 2) return "Bulanan";
            if (period === 3) return "Tahunan";
            return "Harian";
        },
        // Order ini adalah hasil extend bila punya PreviousBookId, atau RootBookId menunjuk chain root
        // lain (root booking di-backfill RootBookId = Id-nya sendiri). CH-W14 extend chain.
        isExtension() {
            const b = this.dataBooking;
            if (!b) return false;
            if (b.PreviousBookId) return true;
            return Boolean(b.RootBookId) && String(b.RootBookId) !== String(b.Id);
        },
        pendingRescheduleData() {
            const pending = this.dataBooking?.PendingReschedule || null;
            if (!pending) return null;
            if (String(pending.ModifyType || "").toUpperCase() !== "RESCHEDULE") return null;
            return pending;
        },
        hasPendingRescheduleUnpaid() {
            const status = String(this.pendingRescheduleData?.PaymentStatus || "").toUpperCase();
            return status === "UNPAID";
        },
        pendingChangeRoomData() {
            const candidates = [
                this.dataBooking?.PendingChangeRoom,
                this.dataBooking?.PendingRoomMove,
                this.dataBooking?.PendingModify,
            ];
            const pending = candidates.find((item) => item) || null;
            if (!pending) return null;
            const modifyType = String(pending?.ModifyType || pending?.Type || "").toUpperCase();
            const isChangeRoomType = ["CHANGE_ROOM", "ROOM_MOVE", "CHANGE ROOM", "ROOMMOVE"].includes(modifyType);
            const hasChangeRoomShape = Boolean(
                pending?.OldRoomCode ||
                pending?.NewRoomCode ||
                pending?.TargetRoomCode ||
                pending?.FromRoomCode ||
                pending?.ToRoomCode
            );
            if (modifyType && !isChangeRoomType && !hasChangeRoomShape) return null;
            return pending;
        },
        pendingChangeRoomFromRequestStorage() {
            if (typeof window === "undefined") return null;
            const bookingCode = String(this.dataBooking?.Code || "").trim();
            if (!bookingCode) return null;
            const records = safeParseArray(window.localStorage.getItem(CHANGE_ROOM_REQUEST_STORAGE_KEY));
            const candidates = records
                .filter((item) => String(item?.bookingCode || "").trim() === bookingCode)
                .filter((item) => !String(item?.id || "").startsWith("CR-DUMMY-"))
                .filter((item) => String(item?.status || "").toUpperCase() === "PROCESSED")
                .filter((item) => String(item?.paymentStatus || "").toUpperCase() === "UNPAID")
                .sort((a, b) => new Date(b?.requestedAt || 0).getTime() - new Date(a?.requestedAt || 0).getTime());
            return candidates[0] || null;
        },
        hasPendingChangeRoomUnpaid() {
            const status = String(this.pendingChangeRoomData?.PaymentStatus || "").toUpperCase();
            if (status === "UNPAID") return true;
            return Boolean(this.pendingChangeRoomFromRequestStorage);
        },
        pendingExtendData() {
            const candidates = [
                this.dataBooking?.PendingExtend,
                this.dataBooking?.PendingModify,
            ];
            const pending = candidates.find((item) => item && typeof item === "object") || null;
            if (!pending) return null;
            const modifyType = String(pending?.ModifyType || pending?.Type || "").toUpperCase();
            if (modifyType && !["EXTEND", "EXTEND_STAY"].includes(modifyType)) return null;
            return pending;
        },
        hasPendingExtendUnpaid() {
            const status = String(this.pendingExtendData?.PaymentStatus || "").toUpperCase();
            return status === "UNPAID";
        }
    },
    methods: {
        ...mapActions("booking",
            [
                "downloadReceipt"
            ]),
        ...mapActions("review", [
            "fetchRoomRatingTypes", // Map action fetchRoomRatingTypes dari module review
            "submitRoomRating" // Map action submitRoomRating dari module review
        ]),
        toggleBookingDetailSelection(id) {
            const selectedDetail = this.dataBooking.BookDetails.find(detail => detail.Id === id);

            if (selectedDetail?.UserRating === null) {
                // Hanya izinkan seleksi jika UserRating null
                this.selectedBookingDetailId = this.selectedBookingDetailId === id ? null : id;
            } else {
                // Jika UserRating tidak null, tampilkan pesan atau abaikan
                this.$alert.toast("error", "This room has already been reviewed.");
            }
        },
        openPaymentSummary(bookingCode) {
            // Set kode booking dan buka modal
            this.selectedBookingCode = bookingCode;
            this.openModalDetail = true;
        },
        handleDetailClick(bookingCode) {
            if (this.openDetailInPage || this.historyMode) {
                const query = this.historyMode ? { from: "history" } : undefined;
                this.$router.push({
                    path: `/booking/${bookingCode}`,
                    query,
                });
                return;
            }
            this.openPaymentSummary(bookingCode);
        },
        closePaymentSummary() {
            // Tutup modal dan reset kode booking
            this.openModalDetail = false;
            this.selectedBookingCode = null;
        },
        async handleDownloadReceipt(bookingCode) {
            try {
                console.log("bookinCode", bookingCode);
                if (!bookingCode || typeof bookingCode !== "string") {
                    console.error("Invalid booking code:", bookingCode);
                    this.$alert.toast("error", "Booking code is invalid.");
                    return;
                }

                const params = this.queryKey ? { key: this.queryKey } : {};

                // Panggil action downloadReceipt dari Vuex
                await this.downloadReceipt({ bookingCode, params });
            } catch (error) {
                console.error("Failed to download receipt:", error);
                this.$alert.toast("error", this.$t("alert.failedToFetchReceipt"));
            }
        },

        openSendReviewModal(bookingCode) {
            this.selectedBookingCode = bookingCode;
            this.isReviewModalOpen = true;
            this.reviewRating = 0; // Reset rating setiap kali modal dibuka
            this.reviewComment = ""; // Reset komentar setiap kali modal dibuka
            this.selectedBookingDetailId = null;

        },
        closeSendReviewModal() {
            this.isReviewModalOpen = false;
            this.selectedBookingCode = null;
            this.hoverRating = 0;
        },
        async submitReview() {
            if (Object.keys(this.selectedRatings).length === 0) {
                this.$alert.toast("error", "Please provide a rating for each category.");
                return;
            }

            if (!this.reviewComment.trim() || this.reviewComment.trim().length < 50) {
                this.$alert.toast("error", this.$t("propertyDetail.minLengthError") || "Description must be at least 50 characters.");
                return;
            }

            try {
                // Buat payload untuk POST
                const payload = {
                    BookingDetailId: this.selectedBookingDetailId,
                    Description: this.reviewComment,
                    RatingDetails: Object.entries(this.selectedRatings).map(([typeId, rating]) => ({
                        RoomRatingTypeId: parseInt(typeId),
                        Rating: rating,
                    })),
                };

                // Kirim payload ke backend
                await this.submitRoomRating(payload);

                this.$alert.toast("success", "Review submitted successfully!");
                this.$emit('close-review');
                this.closeSendReviewModal();
            } catch (error) {
                console.error("Failed to submit review:", error);
                this.$alert.toast("error", "Failed to submit review. Please try again.");
            }
        },
    },
    async mounted() {
        console.log("dataBooking", this.dataBooking);
        try {
            // Fetch room rating types saat komponen dimuat
            const ratingTypes = await this.fetchRoomRatingTypes();
            this.roomRatingTypes = ratingTypes;
            console.log("Room Rating Types (from Component):", ratingTypes); // Log hasil fetch dari komponen
        } catch (error) {
            console.error("Failed to fetch room rating types:", error);
        }
    }
};
</script>

<style scoped>
textarea:disabled {
    background-color: #f3f4f6;
    /* Tailwind Gray-100 */
    cursor: not-allowed;
}
</style>
